import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    Button,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Switch,
    Tabs,
    Tag,
    Timeline,
    Typography,
    message,
    Spin,
    Checkbox,
    DatePicker
} from 'antd';
import {
    PlusOutlined,
    ReloadOutlined,
    SettingOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import Layout from './Layout';
import './InvoiceApprovalPage.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const FIELD_OPTIONS = [
    { value: 'title', label: 'Название' },
    { value: 'amount', label: 'Сумма' },
    { value: 'counterparty', label: 'Контрагент' },
    { value: 'description', label: 'Описание' },
    { value: 'comment', label: 'Комментарий' },
    { value: 'assignee_id', label: 'Ответственный' }
];

const CUSTOM_FIELD_TYPES = [
    { value: 'text', label: 'Текст' },
    { value: 'textarea', label: 'Многострочный' },
    { value: 'number', label: 'Число' },
    { value: 'date', label: 'Дата' },
    { value: 'select', label: 'Список' },
    { value: 'checkbox', label: 'Галочка' },
    { value: 'user', label: 'Сотрудник' }
];

const ROLE_OPTIONS = [
    { value: 'creator', label: 'Создатель' },
    { value: 'participant', label: 'Участник' },
    { value: 'assignee', label: 'Ответственный' },
    { value: 'admin', label: 'Админ' }
];

const money = v =>
    v == null
        ? '—'
        : Number(v).toLocaleString('ru-RU', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
          });

const collectCustomValues = (values, fields) => {
    const custom_values = {};
    (fields || []).forEach(f => {
        if (!Object.prototype.hasOwnProperty.call(values, f.code)) {
            return;
        }
        let v = values[f.code];
        if (f.field_type === 'date' && v && typeof v?.format === 'function') {
            v = v.format('YYYY-MM-DD');
        }
        custom_values[f.code] = v;
    });
    return custom_values;
};

const InvoiceApprovalPage = () => {
    const [loading, setLoading] = useState(true);
    const [funnels, setFunnels] = useState([]);
    const [funnelId, setFunnelId] = useState(null);
    const [board, setBoard] = useState(null);
    const [directory, setDirectory] = useState([]);
    const [canManage, setCanManage] = useState(false);
    const [funnelFields, setFunnelFields] = useState([]);

    const [createOpen, setCreateOpen] = useState(false);
    const [cardOpen, setCardOpen] = useState(false);
    const [activeCard, setActiveCard] = useState(null);
    const [cardRoutes, setCardRoutes] = useState([]);
    const [cardFields, setCardFields] = useState([]);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsFunnel, setSettingsFunnel] = useState(null);

    const [createForm] = Form.useForm();
    const [cardForm] = Form.useForm();
    const [stageForm] = Form.useForm();
    const [routeForm] = Form.useForm();
    const [funnelForm] = Form.useForm();
    const [fieldForm] = Form.useForm();

    const loadDirectory = useCallback(async () => {
        const { data } = await axios.get(
            '/api/invoice-approval/directory',
            { headers: authHeaders() }
        );
        setDirectory(data.users || []);
    }, []);

    const loadFunnels = useCallback(async () => {
        const { data } = await axios.get(
            '/api/invoice-approval/funnels',
            { headers: authHeaders() }
        );
        setFunnels(data.funnels || []);
        setCanManage(Boolean(data.can_manage));
        if (!funnelId && data.funnels?.[0]?.id) {
            setFunnelId(data.funnels[0].id);
        }
        return data.funnels || [];
    }, [funnelId]);

    const loadBoard = useCallback(
        async id => {
            const fid = id || funnelId;
            if (!fid) return;
            setLoading(true);
            try {
                const { data } = await axios.get(
                    '/api/invoice-approval/board',
                    {
                        headers: authHeaders(),
                        params: { funnel_id: fid }
                    }
                );
                setBoard(data);
                setFunnelFields(data.fields || []);
                setCanManage(Boolean(data.can_manage));
            } catch (error) {
                message.error(
                    error.response?.data?.error ||
                        'Не удалось загрузить доску'
                );
            } finally {
                setLoading(false);
            }
        },
        [funnelId]
    );

    useEffect(() => {
        (async () => {
            try {
                await axios.get(
                    '/api/invoice-approval/bootstrap',
                    { headers: authHeaders() }
                );
                await loadDirectory();
                const list = await loadFunnels();
                const id = funnelId || list[0]?.id;
                if (id) {
                    setFunnelId(id);
                    await loadBoard(id);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                message.error(
                    error.response?.data?.error ||
                        'Ошибка модуля'
                );
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (funnelId) loadBoard(funnelId);
    }, [funnelId, loadBoard]);

    const userOptions = useMemo(
        () =>
            directory.map(u => ({
                value: u.id,
                label: u.name
            })),
        [directory]
    );

    const openCard = async id => {
        try {
            const { data } = await axios.get(
                `/api/invoice-approval/cards/${id}`,
                { headers: authHeaders() }
            );
            setActiveCard(data.card);
            setCardRoutes(data.routes || []);
            setCardFields(data.fields || []);
            const cv = data.card.custom_values || {};
            const customInit = {};
            (data.fields || []).forEach(f => {
                let v = cv[f.code];
                if (f.field_type === 'date' && v) v = dayjs(v);
                if (f.field_type === 'checkbox') v = Boolean(v);
                if (f.field_type === 'user' && !v) v = undefined;
                customInit[f.code] = v;
            });
            cardForm.setFieldsValue({
                title: data.card.title,
                amount: data.card.amount,
                counterparty: data.card.counterparty,
                description: data.card.description,
                comment: data.card.comment,
                assignee_id: data.card.assignee_id || undefined,
                participant_ids: (data.card.participants || [])
                    .filter(p => p.role === 'participant')
                    .map(p => p.user_id),
                ...customInit
            });
            setCardOpen(true);
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Нет доступа к карточке'
            );
        }
    };

    const createCard = async () => {
        try {
            const values = await createForm.validateFields();
            const custom_values = collectCustomValues(
                values,
                funnelFields
            );
            const {
                title,
                amount,
                currency,
                counterparty,
                description,
                assignee_id,
                participant_ids
            } = values;
            const { data } = await axios.post(
                '/api/invoice-approval/cards',
                {
                    funnel_id: funnelId,
                    title,
                    amount,
                    currency,
                    counterparty,
                    description,
                    assignee_id,
                    participant_ids,
                    custom_values
                },
                { headers: authHeaders() }
            );
            message.success('Счёт создан');
            setCreateOpen(false);
            createForm.resetFields();
            await loadBoard();
            if (data.card?.id) openCard(data.card.id);
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error ||
                    'Не удалось создать'
            );
        }
    };

    const saveCard = async () => {
        if (!activeCard) return;
        try {
            const values = await cardForm.validateFields();
            const custom_values = collectCustomValues(
                values,
                cardFields
            );
            const {
                title,
                amount,
                counterparty,
                description,
                comment,
                assignee_id,
                participant_ids
            } = values;
            const { data } = await axios.patch(
                `/api/invoice-approval/cards/${activeCard.id}`,
                {
                    title,
                    amount,
                    counterparty,
                    description,
                    comment,
                    assignee_id,
                    participant_ids,
                    custom_values
                },
                { headers: authHeaders() }
            );
            setActiveCard(data.card);
            if (data.fields) setCardFields(data.fields);
            message.success('Сохранено');
            await loadBoard();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error ||
                    'Не удалось сохранить'
            );
        }
    };

    const moveCard = async routeId => {
        if (!activeCard) return;
        try {
            const comment = cardForm.getFieldValue('comment');
            const { data } = await axios.post(
                `/api/invoice-approval/cards/${activeCard.id}/move`,
                { route_id: routeId, comment },
                { headers: authHeaders() }
            );
            setActiveCard(data.card);
            const detail = await axios.get(
                `/api/invoice-approval/cards/${activeCard.id}`,
                { headers: authHeaders() }
            );
            setCardRoutes(detail.data.routes || []);
            if (detail.data.fields) {
                setCardFields(detail.data.fields);
            }
            message.success('Этап обновлён');
            await loadBoard();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Переход недоступен'
            );
        }
    };

    const openSettings = async () => {
        const list = await loadFunnels();
        const f =
            list.find(x => x.id === funnelId) ||
            list[0] ||
            null;
        setSettingsFunnel(f);
        setSettingsOpen(true);
    };

    const refreshSettingsFunnel = async id => {
        const list = await loadFunnels();
        const f = list.find(x => x.id === (id || funnelId));
        setSettingsFunnel(f || null);
    };

    const createFunnel = async () => {
        try {
            const values = await funnelForm.validateFields();
            const { data } = await axios.post(
                '/api/invoice-approval/funnels',
                values,
                { headers: authHeaders() }
            );
            message.success('Воронка создана');
            funnelForm.resetFields();
            await refreshSettingsFunnel(data.funnel?.id);
            if (data.funnel?.id) setFunnelId(data.funnel.id);
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error || 'Ошибка'
            );
        }
    };

    const saveStage = async () => {
        if (!settingsFunnel) return;
        try {
            const values = await stageForm.validateFields();
            await axios.post(
                `/api/invoice-approval/funnels/${settingsFunnel.id}/stages`,
                {
                    id: values.id || undefined,
                    name: values.name,
                    color: values.color,
                    sort_order: values.sort_order,
                    is_initial: values.is_initial,
                    is_final: values.is_final,
                    edit_policy: {
                        editable_fields:
                            values.editable_fields || [],
                        who_can_edit: values.who_can_edit || []
                    }
                },
                { headers: authHeaders() }
            );
            message.success('Этап сохранён');
            stageForm.resetFields();
            await refreshSettingsFunnel(settingsFunnel.id);
            await loadBoard(settingsFunnel.id);
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error || 'Ошибка'
            );
        }
    };

    const editStage = stage => {
        stageForm.setFieldsValue({
            id: stage.id,
            name: stage.name,
            color: stage.color,
            sort_order: stage.sort_order,
            is_initial: stage.is_initial,
            is_final: stage.is_final,
            editable_fields:
                stage.edit_policy?.editable_fields || [],
            who_can_edit: stage.edit_policy?.who_can_edit || []
        });
    };

    const deleteStage = async stageId => {
        if (!settingsFunnel) return;
        try {
            await axios.delete(
                `/api/invoice-approval/funnels/${settingsFunnel.id}/stages/${stageId}`,
                { headers: authHeaders() }
            );
            message.success('Этап удалён');
            await refreshSettingsFunnel(settingsFunnel.id);
            await loadBoard(settingsFunnel.id);
        } catch (error) {
            message.error(
                error.response?.data?.error || 'Ошибка'
            );
        }
    };

    const saveRoute = async () => {
        if (!settingsFunnel) return;
        try {
            const values = await routeForm.validateFields();
            await axios.post(
                `/api/invoice-approval/funnels/${settingsFunnel.id}/routes`,
                {
                    id: values.id || undefined,
                    name: values.name,
                    from_stage_id: values.from_stage_id,
                    to_stage_id: values.to_stage_id,
                    sort_order: values.sort_order,
                    conditions: {
                        require_comment:
                            values.require_comment,
                        min_amount: values.min_amount,
                        max_amount: values.max_amount,
                        allowed_roles:
                            values.allowed_roles || []
                    }
                },
                { headers: authHeaders() }
            );
            message.success('Маршрут сохранён');
            routeForm.resetFields();
            await refreshSettingsFunnel(settingsFunnel.id);
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error || 'Ошибка'
            );
        }
    };

    const editRoute = route => {
        routeForm.setFieldsValue({
            id: route.id,
            name: route.name,
            from_stage_id: route.from_stage_id,
            to_stage_id: route.to_stage_id,
            sort_order: route.sort_order,
            require_comment:
                route.conditions?.require_comment,
            min_amount: route.conditions?.min_amount,
            max_amount: route.conditions?.max_amount,
            allowed_roles:
                route.conditions?.allowed_roles || []
        });
    };

    const deleteRoute = async routeId => {
        if (!settingsFunnel) return;
        try {
            await axios.delete(
                `/api/invoice-approval/funnels/${settingsFunnel.id}/routes/${routeId}`,
                { headers: authHeaders() }
            );
            message.success('Маршрут удалён');
            await refreshSettingsFunnel(settingsFunnel.id);
        } catch (error) {
            message.error(
                error.response?.data?.error || 'Ошибка'
            );
        }
    };

    const saveField = async () => {
        if (!settingsFunnel) return;
        try {
            const values = await fieldForm.validateFields();
            await axios.post(
                `/api/invoice-approval/funnels/${settingsFunnel.id}/fields`,
                {
                    id: values.id || undefined,
                    title: values.title,
                    code: values.code,
                    field_type: values.field_type,
                    options_text: values.options_text,
                    is_required: values.is_required,
                    sort_order: values.sort_order,
                    is_active: values.is_active !== false
                },
                { headers: authHeaders() }
            );
            message.success('Поле сохранено');
            fieldForm.resetFields();
            fieldForm.setFieldsValue({
                field_type: 'text',
                sort_order: 100,
                is_active: true
            });
            await refreshSettingsFunnel(settingsFunnel.id);
            await loadBoard(settingsFunnel.id);
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error || 'Ошибка'
            );
        }
    };

    const editField = field => {
        fieldForm.setFieldsValue({
            id: field.id,
            title: field.title,
            code: field.code,
            field_type: field.field_type,
            options_text: (field.options || []).join('\n'),
            is_required: field.is_required,
            sort_order: field.sort_order,
            is_active: field.is_active !== false
        });
    };

    const deleteField = async fieldId => {
        if (!settingsFunnel) return;
        try {
            await axios.delete(
                `/api/invoice-approval/funnels/${settingsFunnel.id}/fields/${fieldId}`,
                { headers: authHeaders() }
            );
            message.success('Поле удалено');
            await refreshSettingsFunnel(settingsFunnel.id);
            await loadBoard(settingsFunnel.id);
        } catch (error) {
            message.error(
                error.response?.data?.error || 'Ошибка'
            );
        }
    };

    const fieldDisabled = key => {
        if (!activeCard) return true;
        if (activeCard.editable_fields?.includes(key)) {
            return false;
        }
        return true;
    };

    const stageOptions = (settingsFunnel?.stages || []).map(
        s => ({ value: s.id, label: s.name })
    );

    const stageEditableOptions = useMemo(() => {
        const custom = (settingsFunnel?.fields || []).map(f => ({
            value: f.key || `cf:${f.code}`,
            label: f.title
        }));
        return [...FIELD_OPTIONS, ...custom];
    }, [settingsFunnel]);

    const renderCustomFormItems = (fields, { disabledFn } = {}) =>
        (fields || []).map(f => {
            const disabled = disabledFn
                ? disabledFn(f.key || `cf:${f.code}`)
                : false;
            const rules = f.is_required
                ? [{ required: true, message: 'Обязательное поле' }]
                : [];
            if (f.field_type === 'textarea') {
                return (
                    <Form.Item
                        key={f.id}
                        name={f.code}
                        label={f.title}
                        rules={rules}
                    >
                        <TextArea rows={3} disabled={disabled} />
                    </Form.Item>
                );
            }
            if (f.field_type === 'number') {
                return (
                    <Form.Item
                        key={f.id}
                        name={f.code}
                        label={f.title}
                        rules={rules}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            disabled={disabled}
                        />
                    </Form.Item>
                );
            }
            if (f.field_type === 'date') {
                return (
                    <Form.Item
                        key={f.id}
                        name={f.code}
                        label={f.title}
                        rules={rules}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format="DD.MM.YYYY"
                            disabled={disabled}
                        />
                    </Form.Item>
                );
            }
            if (f.field_type === 'select') {
                return (
                    <Form.Item
                        key={f.id}
                        name={f.code}
                        label={f.title}
                        rules={rules}
                    >
                        <Select
                            allowClear
                            disabled={disabled}
                            options={(f.options || []).map(o => ({
                                value: o,
                                label: o
                            }))}
                        />
                    </Form.Item>
                );
            }
            if (f.field_type === 'checkbox') {
                return (
                    <Form.Item
                        key={f.id}
                        name={f.code}
                        label={f.title}
                        valuePropName="checked"
                        rules={
                            f.is_required
                                ? [
                                      {
                                          validator: (_, v) =>
                                              v
                                                  ? Promise.resolve()
                                                  : Promise.reject(
                                                        new Error(
                                                            'Обязательное поле'
                                                        )
                                                    )
                                      }
                                  ]
                                : []
                        }
                    >
                        <Checkbox disabled={disabled} />
                    </Form.Item>
                );
            }
            if (f.field_type === 'user') {
                return (
                    <Form.Item
                        key={f.id}
                        name={f.code}
                        label={f.title}
                        rules={rules}
                    >
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={userOptions}
                            disabled={disabled}
                        />
                    </Form.Item>
                );
            }
            return (
                <Form.Item
                    key={f.id}
                    name={f.code}
                    label={f.title}
                    rules={rules}
                >
                    <Input disabled={disabled} />
                </Form.Item>
            );
        });

    return (
        <Layout>
            <div className="ia-page">
                <div className="ia-page__head">
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Согласование счетов
                        </Title>
                        <Text type="secondary">
                            Карточки видят только участники
                        </Text>
                    </div>
                    <Space wrap>
                        <Select
                            style={{ minWidth: 220 }}
                            value={funnelId || undefined}
                            options={funnels.map(f => ({
                                value: f.id,
                                label: f.title
                            }))}
                            onChange={setFunnelId}
                            placeholder="Воронка"
                        />
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => loadBoard()}
                        >
                            Обновить
                        </Button>
                        {canManage ? (
                            <Button
                                icon={<SettingOutlined />}
                                onClick={openSettings}
                            >
                                Воронки и маршруты
                            </Button>
                        ) : null}
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                createForm.resetFields();
                                setCreateOpen(true);
                            }}
                            disabled={!funnelId}
                        >
                            Новый счёт
                        </Button>
                    </Space>
                </div>

                {loading ? (
                    <div className="ia-page__center">
                        <Spin size="large" />
                    </div>
                ) : !board?.columns?.length ? (
                    <Empty description="Нет этапов в воронке" />
                ) : (
                    <div className="ia-board">
                        {board.columns.map(col => (
                            <div
                                key={col.stage.id}
                                className="ia-column"
                            >
                                <div
                                    className="ia-column__head"
                                    style={{
                                        borderTopColor:
                                            col.stage.color
                                    }}
                                >
                                    <span>{col.stage.name}</span>
                                    <Tag>{col.cards.length}</Tag>
                                </div>
                                <div className="ia-column__body">
                                    {col.cards.length === 0 ? (
                                        <div className="ia-column__empty">
                                            Нет счетов
                                        </div>
                                    ) : (
                                        col.cards.map(card => (
                                            <button
                                                type="button"
                                                key={card.id}
                                                className="ia-card"
                                                onClick={() =>
                                                    openCard(
                                                        card.id
                                                    )
                                                }
                                            >
                                                <div className="ia-card__title">
                                                    {card.title}
                                                </div>
                                                <div className="ia-card__meta">
                                                    {money(
                                                        card.amount
                                                    )}{' '}
                                                    {card.currency}
                                                </div>
                                                {card.counterparty ? (
                                                    <div className="ia-card__meta">
                                                        {
                                                            card.counterparty
                                                        }
                                                    </div>
                                                ) : null}
                                                <div className="ia-card__people">
                                                    {card.assignee
                                                        ?.name ||
                                                        card
                                                            .creator
                                                            ?.name ||
                                                        ''}
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal
                title="Новый счёт"
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onOk={createCard}
                okText="Создать"
                destroyOnClose
                width={560}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    initialValues={{ currency: 'RUB' }}
                >
                    <Form.Item
                        name="title"
                        label="Название"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите название'
                            }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Space
                        style={{ width: '100%' }}
                        size="middle"
                    >
                        <Form.Item
                            name="amount"
                            label="Сумма"
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                            />
                        </Form.Item>
                        <Form.Item
                            name="currency"
                            label="Валюта"
                        >
                            <Input style={{ width: 90 }} />
                        </Form.Item>
                    </Space>
                    <Form.Item
                        name="counterparty"
                        label="Контрагент"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Описание"
                    >
                        <TextArea rows={3} />
                    </Form.Item>
                    <Form.Item
                        name="assignee_id"
                        label="Ответственный"
                    >
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={userOptions}
                        />
                    </Form.Item>
                    <Form.Item
                        name="participant_ids"
                        label="Участники"
                        extra="Только они (и вы) увидят этот счёт"
                    >
                        <Select
                            mode="multiple"
                            showSearch
                            optionFilterProp="label"
                            options={userOptions}
                        />
                    </Form.Item>
                    {renderCustomFormItems(funnelFields)}
                </Form>
            </Modal>

            <Drawer
                title={activeCard?.title || 'Счёт'}
                open={cardOpen}
                onClose={() => {
                    setCardOpen(false);
                    setActiveCard(null);
                }}
                width={520}
                extra={
                    <Button
                        type="primary"
                        onClick={saveCard}
                        disabled={!activeCard?.can_edit}
                    >
                        Сохранить
                    </Button>
                }
            >
                {activeCard ? (
                    <>
                        <Form form={cardForm} layout="vertical">
                            <Form.Item name="title" label="Название">
                                <Input
                                    disabled={fieldDisabled(
                                        'title'
                                    )}
                                />
                            </Form.Item>
                            <Form.Item name="amount" label="Сумма">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    disabled={fieldDisabled(
                                        'amount'
                                    )}
                                />
                            </Form.Item>
                            <Form.Item
                                name="counterparty"
                                label="Контрагент"
                            >
                                <Input
                                    disabled={fieldDisabled(
                                        'counterparty'
                                    )}
                                />
                            </Form.Item>
                            <Form.Item
                                name="description"
                                label="Описание"
                            >
                                <TextArea
                                    rows={3}
                                    disabled={fieldDisabled(
                                        'description'
                                    )}
                                />
                            </Form.Item>
                            <Form.Item
                                name="comment"
                                label="Комментарий"
                            >
                                <TextArea
                                    rows={2}
                                    disabled={fieldDisabled(
                                        'comment'
                                    )}
                                />
                            </Form.Item>
                            <Form.Item
                                name="assignee_id"
                                label="Ответственный"
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={userOptions}
                                    disabled={fieldDisabled(
                                        'assignee_id'
                                    )}
                                />
                            </Form.Item>
                            <Form.Item
                                name="participant_ids"
                                label="Участники"
                            >
                                <Select
                                    mode="multiple"
                                    showSearch
                                    optionFilterProp="label"
                                    options={userOptions}
                                    disabled={
                                        !activeCard.can_edit &&
                                        !activeCard.my_roles?.includes(
                                            'creator'
                                        )
                                    }
                                />
                            </Form.Item>
                            {renderCustomFormItems(cardFields, {
                                disabledFn: fieldDisabled
                            })}
                        </Form>

                        {cardRoutes.length ? (
                            <div className="ia-moves">
                                <Text strong>Переходы</Text>
                                <Space
                                    wrap
                                    style={{ marginTop: 8 }}
                                >
                                    {cardRoutes.map(r => (
                                        <Button
                                            key={r.id}
                                            onClick={() =>
                                                moveCard(r.id)
                                            }
                                        >
                                            {r.name}
                                        </Button>
                                    ))}
                                </Space>
                            </div>
                        ) : null}

                        <div style={{ marginTop: 24 }}>
                            <Text strong>История</Text>
                            <Timeline
                                style={{ marginTop: 12 }}
                                items={(
                                    activeCard.history || []
                                ).map(h => ({
                                    children: (
                                        <div>
                                            <div>
                                                {h.user?.name ||
                                                    '—'}{' '}
                                                · {h.action}
                                            </div>
                                            <Text type="secondary">
                                                {h.comment || ''}
                                            </Text>
                                        </div>
                                    )
                                }))}
                            />
                        </div>
                    </>
                ) : null}
            </Drawer>

            <Drawer
                title="Воронки, этапы и маршруты"
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                width={720}
            >
                <Tabs
                    items={[
                        {
                            key: 'funnels',
                            label: 'Воронки',
                            children: (
                                <>
                                    <Form
                                        form={funnelForm}
                                        layout="vertical"
                                        onFinish={createFunnel}
                                    >
                                        <Form.Item
                                            name="title"
                                            label="Новая воронка"
                                            rules={[
                                                {
                                                    required: true
                                                }
                                            ]}
                                        >
                                            <Input placeholder="Название" />
                                        </Form.Item>
                                        <Form.Item
                                            name="description"
                                            label="Описание"
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                        >
                                            Создать воронку
                                        </Button>
                                    </Form>
                                    <div style={{ marginTop: 16 }}>
                                        {funnels.map(f => (
                                            <Tag
                                                key={f.id}
                                                color={
                                                    f.id ===
                                                    settingsFunnel?.id
                                                        ? 'blue'
                                                        : 'default'
                                                }
                                                style={{
                                                    cursor: 'pointer',
                                                    marginBottom: 8
                                                }}
                                                onClick={() =>
                                                    setSettingsFunnel(
                                                        f
                                                    )
                                                }
                                            >
                                                {f.title}
                                            </Tag>
                                        ))}
                                    </div>
                                </>
                            )
                        },
                        {
                            key: 'stages',
                            label: 'Этапы',
                            children: settingsFunnel ? (
                                <>
                                    <Text type="secondary">
                                        Воронка:{' '}
                                        {settingsFunnel.title}
                                    </Text>
                                    <Form
                                        form={stageForm}
                                        layout="vertical"
                                        style={{ marginTop: 12 }}
                                        onFinish={saveStage}
                                    >
                                        <Form.Item
                                            name="id"
                                            hidden
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="name"
                                            label="Название этапа"
                                            rules={[
                                                {
                                                    required: true
                                                }
                                            ]}
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Space wrap>
                                            <Form.Item
                                                name="color"
                                                label="Цвет"
                                                initialValue="#2f6fed"
                                            >
                                                <Input
                                                    type="color"
                                                    style={{
                                                        width: 60
                                                    }}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                name="sort_order"
                                                label="Порядок"
                                                initialValue={100}
                                            >
                                                <InputNumber />
                                            </Form.Item>
                                            <Form.Item
                                                name="is_initial"
                                                label="Старт"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>
                                            <Form.Item
                                                name="is_final"
                                                label="Финал"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>
                                        </Space>
                                        <Form.Item
                                            name="editable_fields"
                                            label="Какие поля можно редактировать на этапе"
                                        >
                                            <Checkbox.Group
                                                options={
                                                    stageEditableOptions
                                                }
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name="who_can_edit"
                                            label="Кто может редактировать"
                                        >
                                            <Checkbox.Group
                                                options={
                                                    ROLE_OPTIONS
                                                }
                                            />
                                        </Form.Item>
                                        <Space>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                            >
                                                Сохранить этап
                                            </Button>
                                            <Button
                                                onClick={() =>
                                                    stageForm.resetFields()
                                                }
                                            >
                                                Сброс
                                            </Button>
                                        </Space>
                                    </Form>
                                    <div className="ia-settings-list">
                                        {(
                                            settingsFunnel.stages ||
                                            []
                                        ).map(s => (
                                            <div
                                                key={s.id}
                                                className="ia-settings-row"
                                            >
                                                <span
                                                    className="ia-settings-dot"
                                                    style={{
                                                        background:
                                                            s.color
                                                    }}
                                                />
                                                <span>
                                                    {s.name}
                                                    {s.is_initial
                                                        ? ' · старт'
                                                        : ''}
                                                    {s.is_final
                                                        ? ' · финал'
                                                        : ''}
                                                </span>
                                                <Space>
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            editStage(
                                                                s
                                                            )
                                                        }
                                                    >
                                                        Изменить
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        danger
                                                        icon={
                                                            <DeleteOutlined />
                                                        }
                                                        onClick={() =>
                                                            deleteStage(
                                                                s.id
                                                            )
                                                        }
                                                    />
                                                </Space>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <Empty description="Выберите воронку" />
                            )
                        },
                        {
                            key: 'fields',
                            label: 'Поля',
                            children: settingsFunnel ? (
                                <>
                                    <Text type="secondary">
                                        Свои поля воронки «
                                        {settingsFunnel.title}»
                                    </Text>
                                    <Form
                                        form={fieldForm}
                                        layout="vertical"
                                        style={{ marginTop: 12 }}
                                        onFinish={saveField}
                                        initialValues={{
                                            field_type: 'text',
                                            sort_order: 100,
                                            is_active: true
                                        }}
                                    >
                                        <Form.Item
                                            name="id"
                                            hidden
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="title"
                                            label="Название поля"
                                            rules={[
                                                {
                                                    required: true
                                                }
                                            ]}
                                        >
                                            <Input placeholder="Номер счёта" />
                                        </Form.Item>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(
                                                prev,
                                                cur
                                            ) => prev.id !== cur.id}
                                        >
                                            {() => (
                                                <Form.Item
                                                    name="code"
                                                    label="Код"
                                                    extra="Латиница, цифры и _. При создании можно оставить пустым"
                                                >
                                                    <Input
                                                        placeholder="invoice_no"
                                                        disabled={Boolean(
                                                            fieldForm.getFieldValue(
                                                                'id'
                                                            )
                                                        )}
                                                    />
                                                </Form.Item>
                                            )}
                                        </Form.Item>
                                        <Space wrap>
                                            <Form.Item
                                                name="field_type"
                                                label="Тип"
                                                rules={[
                                                    {
                                                        required: true
                                                    }
                                                ]}
                                            >
                                                <Select
                                                    style={{
                                                        width: 180
                                                    }}
                                                    options={
                                                        CUSTOM_FIELD_TYPES
                                                    }
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                name="sort_order"
                                                label="Порядок"
                                            >
                                                <InputNumber />
                                            </Form.Item>
                                            <Form.Item
                                                name="is_required"
                                                label="Обязательное"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>
                                            <Form.Item
                                                name="is_active"
                                                label="Активно"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>
                                        </Space>
                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(
                                                prev,
                                                cur
                                            ) =>
                                                prev.field_type !==
                                                cur.field_type
                                            }
                                        >
                                            {() =>
                                                fieldForm.getFieldValue(
                                                    'field_type'
                                                ) === 'select' ? (
                                                    <Form.Item
                                                        name="options_text"
                                                        label="Варианты списка"
                                                        extra="Каждый вариант с новой строки"
                                                    >
                                                        <TextArea
                                                            rows={4}
                                                        />
                                                    </Form.Item>
                                                ) : null
                                            }
                                        </Form.Item>
                                        <Space>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                            >
                                                Сохранить поле
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    fieldForm.resetFields();
                                                    fieldForm.setFieldsValue(
                                                        {
                                                            field_type:
                                                                'text',
                                                            sort_order: 100,
                                                            is_active: true
                                                        }
                                                    );
                                                }}
                                            >
                                                Сброс
                                            </Button>
                                        </Space>
                                    </Form>
                                    <div className="ia-settings-list">
                                        {(
                                            settingsFunnel.fields ||
                                            []
                                        ).map(f => (
                                            <div
                                                key={f.id}
                                                className="ia-settings-row"
                                            >
                                                <span>
                                                    {f.title}
                                                    <Text
                                                        type="secondary"
                                                        style={{
                                                            marginLeft: 8
                                                        }}
                                                    >
                                                        {f.field_type}
                                                        {f.is_required
                                                            ? ' · обяз.'
                                                            : ''}
                                                    </Text>
                                                </span>
                                                <Space>
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            editField(
                                                                f
                                                            )
                                                        }
                                                    >
                                                        Изменить
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        danger
                                                        icon={
                                                            <DeleteOutlined />
                                                        }
                                                        onClick={() =>
                                                            deleteField(
                                                                f.id
                                                            )
                                                        }
                                                    />
                                                </Space>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <Empty description="Выберите воронку" />
                            )
                        },
                        {
                            key: 'routes',
                            label: 'Маршруты',
                            children: settingsFunnel ? (
                                <>
                                    <Form
                                        form={routeForm}
                                        layout="vertical"
                                        onFinish={saveRoute}
                                    >
                                        <Form.Item
                                            name="id"
                                            hidden
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="name"
                                            label="Название перехода"
                                            rules={[
                                                {
                                                    required: true
                                                }
                                            ]}
                                        >
                                            <Input placeholder="Утвердить" />
                                        </Form.Item>
                                        <Space
                                            style={{
                                                width: '100%'
                                            }}
                                            wrap
                                        >
                                            <Form.Item
                                                name="from_stage_id"
                                                label="Откуда"
                                                rules={[
                                                    {
                                                        required: true
                                                    }
                                                ]}
                                            >
                                                <Select
                                                    style={{
                                                        width: 200
                                                    }}
                                                    options={
                                                        stageOptions
                                                    }
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                name="to_stage_id"
                                                label="Куда"
                                                rules={[
                                                    {
                                                        required: true
                                                    }
                                                ]}
                                            >
                                                <Select
                                                    style={{
                                                        width: 200
                                                    }}
                                                    options={
                                                        stageOptions
                                                    }
                                                />
                                            </Form.Item>
                                        </Space>
                                        <Form.Item
                                            name="require_comment"
                                            label="Нужен комментарий"
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>
                                        <Space>
                                            <Form.Item
                                                name="min_amount"
                                                label="Мин. сумма"
                                            >
                                                <InputNumber />
                                            </Form.Item>
                                            <Form.Item
                                                name="max_amount"
                                                label="Макс. сумма"
                                            >
                                                <InputNumber />
                                            </Form.Item>
                                        </Space>
                                        <Form.Item
                                            name="allowed_roles"
                                            label="Кто может выполнить переход"
                                        >
                                            <Checkbox.Group
                                                options={
                                                    ROLE_OPTIONS
                                                }
                                            />
                                        </Form.Item>
                                        <Space>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                            >
                                                Сохранить маршрут
                                            </Button>
                                            <Button
                                                onClick={() =>
                                                    routeForm.resetFields()
                                                }
                                            >
                                                Сброс
                                            </Button>
                                        </Space>
                                    </Form>
                                    <div className="ia-settings-list">
                                        {(
                                            settingsFunnel.routes ||
                                            []
                                        ).map(r => (
                                            <div
                                                key={r.id}
                                                className="ia-settings-row"
                                            >
                                                <span>
                                                    {r.name} (
                                                    {
                                                        settingsFunnel.stages?.find(
                                                            s =>
                                                                s.id ===
                                                                r.from_stage_id
                                                        )
                                                            ?.name
                                                    }{' '}
                                                    →{' '}
                                                    {
                                                        settingsFunnel.stages?.find(
                                                            s =>
                                                                s.id ===
                                                                r.to_stage_id
                                                        )
                                                            ?.name
                                                    }
                                                    )
                                                </span>
                                                <Space>
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            editRoute(
                                                                r
                                                            )
                                                        }
                                                    >
                                                        Изменить
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        danger
                                                        icon={
                                                            <DeleteOutlined />
                                                        }
                                                        onClick={() =>
                                                            deleteRoute(
                                                                r.id
                                                            )
                                                        }
                                                    />
                                                </Space>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <Empty description="Выберите воронку" />
                            )
                        }
                    ]}
                />
            </Drawer>
        </Layout>
    );
};

export default InvoiceApprovalPage;
